import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Text, StyleSheet, Platform, Pressable,
} from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import { shallowEqual } from 'react-redux/es/exports';
import TicketItemWrapper from './TicketItemWrapper';
import Colors from '../../../../../constants/Colors';
import isMobile from '../../../../../components/IsMobile';
import { logEvent } from '../../../../../components/LogEvent';
import getPriorityColor from '../../../../../components/PriorityColorScheme';
import {
  DEFAULT_TICKET_PRIORITY, Ticket, ticketPriorityIsValid, TicketStatus,
} from '../../../../../constants/Types';
import PinIcon from '../../../../../components/24pxIcons/PinIcon';
import SlaIcon from '../../../../../components/18pxIcons/SlaIcon';
import Style from '../../../../../constants/Style';
import { Building } from '../../../../LocationManagement/components/AddBuildingModal';
import { BuildingSettingsPriority } from '../../../../LocationManagement/components/BuildingSettingsModal/data';
import AvatarIcon16 from '../../../../../components/16pxIcons/AvatarIcon16';

interface TicketItemProps {
  ticket: Ticket,
  selectedTicketId: string,
  setSelectedTicket: Dispatch<SetStateAction<Ticket>>,
  setModalVisible: Dispatch<SetStateAction<boolean>>,
  pinnedTickets: Ticket[],
  updatePinnedTickets: Dispatch<SetStateAction<Ticket[]>>,
  originalFetchTime,
  index: number,
}

const TicketItem = ({
  ticket, selectedTicketId, setSelectedTicket, setModalVisible, index, originalFetchTime,
  updatePinnedTickets, pinnedTickets,
}: TicketItemProps) => {
  const isSelected: boolean = Platform.OS === 'web' && ticket.id === selectedTicketId;
  const isPinned: boolean = (pinnedTickets ?? []).some((t: Ticket) => t.id === ticket.id);
  const slaNames: string[] = useSelector((state: { selectedBuilding: Building }) => state
    .selectedBuilding.SlaHours?.map((item: BuildingSettingsPriority) => item.Name), shallowEqual);

  let { priority } = ticket;
  if (!ticketPriorityIsValid(priority)) priority = DEFAULT_TICKET_PRIORITY;

  const getStatusLabel = () => {
    if (ticket.status === TicketStatus.COMPLETED) return 'Resolved';
    if (ticket.status === TicketStatus.INPROGRESS || ticket.status === TicketStatus.PAUSED) {
      return 'In Progress';
    }
    return 'Open';
  };

  const getStatusBorderColor = () => {
    if (ticket.status === TicketStatus.COMPLETED) return Colors.light.hereworksGreen300;
    if (ticket.status === TicketStatus.INPROGRESS || ticket.status === TicketStatus.PAUSED) {
      return Colors.light.hereworksBlue500;
    }
    return Colors.light.hereworksBlue300;
  };

  const getStatusTextColor = () => {
    if (ticket.status === TicketStatus.COMPLETED) return Colors.light.hereworksGreen500;
    if (ticket.status === TicketStatus.INPROGRESS || ticket.status === TicketStatus.PAUSED) {
      return Colors.light.hereworksBlue500;
    }
    return Colors.light.hereworksBlack500;
  };

  const getStatusBackgroundColor = () => {
    if (ticket.status === TicketStatus.COMPLETED) return Colors.light.hereworksGreen50;
    if (ticket.status === TicketStatus.INPROGRESS || ticket.status === TicketStatus.PAUSED) {
      return Colors.light.hereworksBlue50;
    }
    return Colors.light.plainBackground;
  };

  const getSlaDueMoment = () => {
    const dueMoment = Moment(ticket.dueDate);
    if (dueMoment.isValid()) return dueMoment;
    return Moment(ticket.createdOn)
      .add(ticket.slaHours || 0, 'hours')
      .add(ticket.slaMinutes || 0, 'minutes');
  };

  const getSlaTagTextParts = () => {
    const dueMoment = getSlaDueMoment();
    const now = Moment();
    const hours = Math.ceil(Math.abs(dueMoment.diff(now, 'minutes')) / 60);
    if (dueMoment.isBefore(now)) {
      return { prefix: 'Response overdue ', time: `${hours}h` };
    }
    return { prefix: 'Response due ', time: `${hours}h` };
  };

  const getSlaTagStyles = () => {
    const dueMoment = getSlaDueMoment();
    if (dueMoment.isBefore(Moment())) {
      return {
        borderColor: Colors.light.hereworksRed200,
        backgroundColor: Colors.light.plainBackground,
        textColor: Colors.light.hereworksRed700,
      };
    }
    return {
      borderColor: Colors.light.hereworksBlue200,
      backgroundColor: Colors.light.plainBackground,
      textColor: Colors.light.hereworksBlue500,
    };
  };

  const slaTagStyles = getSlaTagStyles();
  const slaTagTextParts = getSlaTagTextParts();
  const assigneeText = ticket.assignee?.trim()?.length ? ticket.assignee : 'Not assigned';

  return <TicketItemWrapper
    onPress={() => {
      logEvent('TicketItem', 'Ticket_Selected');
      if (selectedTicketId === ticket.id) {
        setSelectedTicket(null);
        setModalVisible(false);
        return;
      }
      setSelectedTicket(ticket);
      setModalVisible(true);
    }}
    selected={isSelected}
    style={[
      styles.listTicketItem,
      !isSelected && {
        backgroundColor: Moment(ticket.createdOn) > originalFetchTime
          ? Colors.light.hereworksWhite100 : '#fff',
        borderColor: Moment(ticket.createdOn) > originalFetchTime ? Colors.light.hereworksBlue300
          : Colors.light.hereworksWhite300,
      },
      isSelected && { borderColor: Colors.light.hereworksBlue500 },
    ]}
    {...{ index }}
    status={ticket.status}
  >
    <View style={{ flexDirection: 'row', marginBottom: 6, alignSelf: 'stretch' }}>
      <View style={[Style.row,
        styles.statusWrapper,
        {
          borderColor: getStatusBorderColor(),
          backgroundColor: getStatusBackgroundColor(),
        }]}>
        <Text style={[
          styles.statusText,
          { color: getStatusTextColor() },
        ]}>{getStatusLabel()}</Text>
      </View>
      <View style={[Style.row, styles.priorityWrapper]}>
        <View
          style={[
            styles.priorityCircle,
            { backgroundColor: getPriorityColor(priority) },
          ]}
        />
        <Text style={styles.slaName}>{slaNames[priority]}</Text>
      </View>
      <View style={{
        flex: 1, justifyContent: 'flex-end', alignItems: 'center', flexDirection: 'row',
      }}>
        {!isMobile && <Text
          style={[
            styles.ticketTime,
          ]}>
          {Moment(ticket.createdOn).fromNow()}
        </Text>}
        <Pressable style={(state) => [{
          backgroundColor: state.hovered ? Colors.light.hereworksBlue50 : 'transparent',
          padding: 2,
          borderRadius: 4,
        }]}
          onPress={() => {
            if (isPinned) updatePinnedTickets(pinnedTickets.filter((t) => t.id !== ticket.id));
            else updatePinnedTickets([...(pinnedTickets ?? []), ticket]);
          }}
        >
          <PinIcon fill={isPinned ? Colors.light.hereworksBlue200 : 'none'} />
        </Pressable>
      </View>
    </View>
    <View style={styles.itemDetails}>
      <View style={{ }}>
        <Text
          numberOfLines={1}
          style={[
            styles.ticketTitle,
          ]}
        >
          {ticket.name}
          {/* {ticket.images && ticket.images.length > 0 ? ' 📷' : ''} */}
        </Text>
      </View>
      <View style={{}}>
        <Text
          style={[
            styles.ticketArea,
          ]}
          numberOfLines={1}
        >
          {`${ticket.locationName ?? ''} | ${ticket.floorName ?? ''}`}
        </Text>
      </View>
      <View style={styles.slaAssigneeRow}>
        <View
          style={[
            styles.slaTag,
            {
              borderColor: slaTagStyles.borderColor,
              backgroundColor: slaTagStyles.backgroundColor,
            },
          ]}
        >
          <SlaIcon color={slaTagStyles.textColor} style={styles.slaIcon} />
          <Text style={{ color: slaTagStyles.textColor }}>
            <Text style={styles.slaTagTextPrefix}>{slaTagTextParts.prefix}</Text>
            <Text style={styles.slaTagText}>{slaTagTextParts.time}</Text>
          </Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={{ borderRadius: 8, overflow: 'hidden' }}><AvatarIcon16 /></View>
          <Text numberOfLines={1} style={styles.assigneeText}>{`${assigneeText}`}</Text>
        </View>
      </View>
    </View>
  </TicketItemWrapper>;
};

const styles = StyleSheet.create({
  shadow: {
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.05,
    shadowRadius: 20,
    shadowColor: 'black',
    elevation: 5,
  },
  ticketArea: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack400,
  },
  ticketTime: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    marginRight: 8,
    color: Colors.light.hereworksBlack400,
  },
  listTicketItem: {
    marginVertical: 6,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 2,
    padding: 15,
    borderColor: Colors.light.hereworksWhite300,
    height: 134,
  },
  statusWrapper: {
    alignItems: 'center',
    borderWidth: 1,
    paddingVertical: 1,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  statusText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 20,
    color: Colors.light.hereworksBlack500,
  },
  priorityCircle: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  ticketTitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksPrimaryGrey,
    paddingRight: 6,
    flex: 1,
  },
  ticketArrowIcon: {
    position: 'absolute',
    right: 20,
  },
  itemDetails: {
    alignSelf: 'stretch',
  },
  slaName: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
    color: Colors.light.hereworksBlack600,
    marginLeft: 4,
  },
  priorityWrapper: {
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite600,
    paddingVertical: 1,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: Colors.light.plainBackground,
  },
  slaTag: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  slaAssigneeRow: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  assigneeText: {
    flex: 1,
    marginLeft: 8,
    textAlign: 'right',
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack400,
  },
  slaIcon: {
    marginRight: 4,
  },
  slaTagText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 18,
  },
  slaTagTextPrefix: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 18,
  },
});

export default TicketItem;
